import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { ThemeContext } from 'styled-components';
import SectionDivider from 'modules/shared/common/components/SectionDivider';

import { paymentMethodsKeys } from 'constants/general';
import {
  OrderListNoResultsWrapper,
  OrderListNoResultsText,
  OrdersListWrapper,
  OrdersListGridWrapper,
  OrdersListHeader,
  OrdersListDivider,
  OrdersListItem,
  OrdersListItemLink,
  OrdersListItemText,
  OrdersListItemSubText,
  OrderListItemStatus,
  DelayedOrder,
  DelayedOrderTextAlert,
  DelayedOrderIcon,
  DelayedOrderWrapper,
  OrderListItemStatusReason,
  OrderInfoSticker,
} from './OrdersList.style';

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const OrdersList = ({
  mappedOrdersList,
  orderStatusColorEnum,
  stringsData,
  partnershipTypes,
  showPartnersColumn,
  showPaymentColumn,
  entityTypeId,
  orderStatusEnum,
}) => {
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const themeContext = useContext(ThemeContext);
  const { colors } = themeContext;
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################

  const renderOrderNumber = order => {
    const delayArr = [];
    if (order.processingDelayCounter) delayArr.push('Processing');
    if (order.deliveryDelayCounter) delayArr.push('Delivery');
    const delayText = delayArr.join(' & ');
    return delayText ? (
      <DelayedOrder>
        <DelayedOrderWrapper>
          <OrdersListItemLink to={`/order/${order.key}`} target="_blank">
            <OrdersListItemText>{order.orderNumber}</OrdersListItemText>
          </OrdersListItemLink>
          <DelayedOrderIcon />
        </DelayedOrderWrapper>
        <DelayedOrderTextAlert>{delayText} delay</DelayedOrderTextAlert>
      </DelayedOrder>
    ) : (
      <OrdersListItemLink to={`/order/${order.key}`} target="_blank">
        <OrdersListItemText>{order.orderNumber}</OrdersListItemText>
      </OrdersListItemLink>
    );
  };

  const renderOrderStatus = (order, color, backgroundColor) => {
    // in case of pharmacy scheduled status alter the default color and text
    let tempBackgroundColor = backgroundColor;
    let tempOrderStatusStr = order.orderStatusStr;
    if (order.orderStateTypeId === orderStatusEnum?.Scheduled) {
      if (order.orderStates?.length > 0) {
        const tempEntityType =
          entityTypeId[order.orderStates[order.orderStates.length - 1]?.entityTypeId];
        tempBackgroundColor = tempEntityType?.color;
        tempOrderStatusStr = `${tempEntityType?.name} ${order.orderStatusStr}`;
      }
    }
    return (
      <OrderListItemStatus color={color} backgroundColor={tempBackgroundColor}>
        {tempOrderStatusStr}
      </OrderListItemStatus>
    );
  };

  const renderOrderStateReason = order => {
    const orderReasonObj = order?.orderStates[order.orderStates.length - 1];
    const reason = orderReasonObj?.orderStateReason?.orderStateChildReason?.name;
    return (
      orderReasonObj && (
        <OrderListItemStatusReason title={reason}>{reason}</OrderListItemStatusReason>
      )
    );
  };

  const renderOrderAppliedSticker = order => {
    let orderStickerInfo = {};
    if (order.promoCode) {
      orderStickerInfo = {
        icon: "'\\e911'",
        color: 'blueMain',
        title: order.promoCode,
      };
    } else if (order.optionalPaymentMethodKey === paymentMethodsKeys.vezeetaCash) {
      orderStickerInfo = {
        icon: "'\\e912'",
        color: 'orangeDark',
        title: stringsData.ordersList.loyaltyPointsStickerText,
      };
    } else {
      orderStickerInfo = {
        title: '-',
      };
    }
    return (
      <OrderInfoSticker icon={orderStickerInfo?.icon} color={colors[orderStickerInfo?.color]}>
        {orderStickerInfo.title}
      </OrderInfoSticker>
    );
  };

  const renderOrderPartnersSticker = order => {
    let orderStickerInfo = {};
    const orderPartnershipType = partnershipTypes?.details?.find(
      partnership => partnership.key === order?.partnershipTypeKey,
    );
    if (orderPartnershipType) {
      const isPartnershipBD = orderPartnershipType?.name?.toLowerCase().includes('bd');
      orderStickerInfo = {
        icon: isPartnershipBD ? "'\\e917'" : "'\\e90d'",
        title: `${orderPartnershipType.name}`,
        color: isPartnershipBD ? 'purpleMain' : 'greenMain',
      };
    } else {
      orderStickerInfo = {
        title: '-',
      };
    }

    return (
      <OrderInfoSticker icon={orderStickerInfo?.icon} color={colors[orderStickerInfo?.color]}>
        {orderStickerInfo.title}
      </OrderInfoSticker>
    );
  };

  const renderOrderPaymentSticker = order => {
    let orderStickerInfo = {};
    if (order.onlinePaymentCardKey) {
      orderStickerInfo = {
        icon: "'\\e91a'",
        title: stringsData.ordersList.creditCardStickerText,
        color: 'purpleMain',
      };
    } else {
      orderStickerInfo = {
        icon: "'\\e919'",
        title: stringsData.ordersList.cashStickerText,
        color: 'greenMain',
      };
    }

    return (
      <OrderInfoSticker icon={orderStickerInfo?.icon} color={colors[orderStickerInfo?.color]}>
        {orderStickerInfo.title}
      </OrderInfoSticker>
    );
  };

  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  const orderGridHeaderList = stringsData.ordersList.orderGridHeader;

  if (mappedOrdersList.length === 0)
    return (
      <OrderListNoResultsWrapper>
        <OrderListNoResultsText>
          {stringsData.ordersList.orderGridNoResultsFound}
        </OrderListNoResultsText>
      </OrderListNoResultsWrapper>
    );

  return (
    <OrdersListWrapper>
      <OrdersListGridWrapper columnsCount={Object.keys(orderGridHeaderList).length}>
        <OrdersListDivider columnsCount={Object.keys(orderGridHeaderList).length} />
        {Object.keys(orderGridHeaderList).map(orderGridHeaderKey => (
          <OrdersListHeader key={orderGridHeaderKey}>
            {orderGridHeaderList[orderGridHeaderKey]}
          </OrdersListHeader>
        ))}
        <OrdersListDivider />
        {mappedOrdersList.map(order => {
          const { color = colors.purpleDark, backgroundColor = colors.purpleLight } =
            orderStatusColorEnum(colors)[order.orderStateTypeId] || {};

          return (
            <React.Fragment key={order.orderNumber}>
              <OrdersListItem>{renderOrderNumber(order)}</OrdersListItem>
              <OrdersListItem>
                {renderOrderStatus(order, color, backgroundColor)}
                {renderOrderStateReason(order)}
              </OrdersListItem>
              <OrdersListItem>
                <OrdersListItemText>{order.orderDateTimeStr}</OrdersListItemText>
                <SectionDivider gap={2} />
                <OrdersListItemSubText>{order.orderElapsedTimeStr}</OrdersListItemSubText>
              </OrdersListItem>
              <OrdersListItem>
                <OrdersListItemText>{order.recipientName}</OrdersListItemText>
                <SectionDivider gap={2} />
                <OrdersListItemSubText>{order.recipientNumber}</OrdersListItemSubText>
              </OrdersListItem>
              <OrdersListItem>
                <OrdersListItemText>{order.source}</OrdersListItemText>
              </OrdersListItem>
              {showPartnersColumn && (
                <OrdersListItem>{renderOrderPartnersSticker(order)}</OrdersListItem>
              )}
              <OrdersListItem>{renderOrderAppliedSticker(order)}</OrdersListItem>
              <OrdersListItem>
                <OrdersListItemText>{order.priceDetails.netTotal.toFixed(2)}</OrdersListItemText>
                <SectionDivider gap={2} />
                <OrdersListItemSubText>
                  {`${order.orderItems.length} ${stringsData.ordersList.orderGridItemCount}`}
                </OrdersListItemSubText>
              </OrdersListItem>
              {showPaymentColumn && (
                <OrdersListItem>{renderOrderPaymentSticker(order)}</OrdersListItem>
              )}
              <OrdersListItem>
                <OrdersListItemText>{order.selectedLabObj?.name || '-'}</OrdersListItemText>
                <SectionDivider gap={2} />
                <OrdersListItemSubText>
                  {order.selectedChemistObj?.name || '-'}
                </OrdersListItemSubText>
              </OrdersListItem>
              <OrdersListDivider />
            </React.Fragment>
          );
        })}
      </OrdersListGridWrapper>
    </OrdersListWrapper>
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

OrdersList.propTypes = {
  mappedOrdersList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  orderStatusColorEnum: PropTypes.func.isRequired,
  stringsData: PropTypes.shape({
    ordersList: PropTypes.shape({
      orderGridNoResultsFound: PropTypes.string,
      orderGridHeader: PropTypes.shape({}),
      orderGridItemCount: PropTypes.string,
      loyaltyPointsStickerText: PropTypes.string,
      creditCardStickerText: PropTypes.string,
      cashStickerText: PropTypes.string,
    }),
  }).isRequired,
  partnershipTypes: PropTypes.shape({
    details: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  showPartnersColumn: PropTypes.bool,
  showPaymentColumn: PropTypes.bool,
  entityTypeId: PropTypes.arrayOf(
    PropTypes.shape({
      color: PropTypes.string,
      name: PropTypes.string,
    }),
  ).isRequired,
  orderStatusEnum: PropTypes.shape({
    Scheduled: PropTypes.number,
  }).isRequired,
};

OrdersList.defaultProps = {
  partnershipTypes: {},
  showPartnersColumn: false,
  showPaymentColumn: false,
};

export default OrdersList;
